import { Navigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtTs } from './AlphaOmegaDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

export interface SectorSignalResponse {
  signal_id: string;
  sector: string;
  signal_type: string;
  confidence_bps: number;
  summary: string;
  captured_at: number;
}

interface SectorSignalsResponse {
  signals: SectorSignalResponse[];
  total: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function signalTypeColor(signal_type: string): string {
  switch (signal_type) {
    case 'growth':    return '#34d399';
    case 'risk':      return '#f87171';
    case 'neutral':   return '#94a3b8';
    case 'opportun':  return '#7dd3fc';
    default:          return 'var(--omega-ink)';
  }
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaSectorSignalsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [weekInput, setWeekInput] = useState('');

  const queryKey = weekInput
    ? ['alpha-omega', 'sector-signals', 'week', weekInput]
    : ['alpha-omega', 'sector-signals'];

  const queryPath = weekInput
    ? `/alpha-omega/sector-signals?week=${weekInput}`
    : '/alpha-omega/sector-signals';

  const { data, isPending, isError } = useQuery({
    queryKey,
    queryFn: () => apiFetch<SectorSignalsResponse>(queryPath),
    refetchInterval: 30_000,
  });

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',       to: '/omega' },
        { label: 'Alpha-Omega', to: '/omega/alpha-omega' },
        { label: 'Market signals' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Sector Signals
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>Week (unix):</label>
          <input
            type="number"
            value={weekInput}
            onChange={e => setWeekInput(e.target.value)}
            placeholder="Optional"
            style={{ ...inputStyle, width: 120 }}
          />
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading signals.</div>}

      {data && (
        <>
          <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', marginBottom: 12 }}>
            {data.total} Signals
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 800 }}>
            {data.signals.map(s => (
              <Link
                key={s.signal_id}
                to={`/omega/alpha-omega/sector-signals/${s.signal_id}`}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 110px 80px 100px',
                  gap: 12, padding: '10px 14px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                  borderRadius: 4, textDecoration: 'none', color: 'var(--omega-ink)',
                }}
              >
                <span style={{ fontSize: '8.5pt', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.summary}</span>
                <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>{s.sector}</span>
                <span style={{ fontSize: '8.5pt', color: signalTypeColor(s.signal_type), fontFamily: 'var(--omega-mono)', textTransform: 'uppercase' }}>{s.signal_type}</span>
                <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>{fmtTs(s.captured_at)}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
